// course-detail.component.ts
import { Component, OnInit, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { COURSE_ENTITY_SERVICE_TOKEN } from '../../../../injection-tokens/entity-service-tokens';
import { ICourseEntityService } from '../../../../interfaces/entities/course-entity-service.interface';
import { CourseEntity } from '../../../../models/entities/course';
import { ErrorHandlerService } from '../../../../decoration-base/error-handler.service';
import { NotificationService } from '../../../../services/notification.service';
import { Log, IShortLogger } from '../../../../services/logger.service';

interface LabItem {
  code: string;
  name: string;
  description: string;
}

@Component({
  selector: 'app-course-detail',
  templateUrl: './course-detail.component.html',
  styleUrl: './course-detail.component.css'
})
export class CourseDetailComponent implements OnInit {
  private readonly logger: IShortLogger<CourseDetailComponent> = Log.create(CourseDetailComponent);

  course: CourseEntity | undefined;
  loading: boolean = true;
  error: string | null = null;
  courseId: string = '';

  // Collapsible panel states
  courseInfoCollapsed = false;
  vmConfigCollapsed = false;

  selectedLab = 0;

  // Sample data for labs
  labsList: LabItem[] = [
    {
      code: 'LAB03',
      name: 'Building RESTful API with ASP.NET Core',
      description: 'Learn how to create a RESTful API using ASP.NET Core, configure routing, dependency injection, and implement CRUD operations.'
    },
    {
      code: 'LAB04',
      name: 'Entity Framework Core Integration',
      description: 'Connect your ASP.NET Core API to SQL Server using Entity Framework Core. Set up models, DbContext, and migrations.'
    },
    {
      code: 'LAB05',
      name: 'Authentication and Authorization',
      description: 'Implement JWT authentication and role-based authorization in your ASP.NET Core API.'
    }
  ];

  // Software tools for VM
  softwareTools: string[] = [
    '.NET 8',
    'SQL Server 2022',
    'VS Code',
    'Visual Studio 2022',
    'Postman',
    'Git'
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    @Inject(COURSE_ENTITY_SERVICE_TOKEN) public courseService: ICourseEntityService,
    private errorHandler: ErrorHandlerService,
    private notification: NotificationService
  ) { }

  ngOnInit(): void {
    // Get course ID from route parameters
    this.route.params.subscribe(params => {
      this.courseId = params['id'];
      if (this.courseId) {
        this.loadCourseDetails();
      } else {
        this.error = 'No course ID provided';
        this.loading = false;
      }
    });
  }

  loadCourseDetails(): void {
    this.loading = true;
    this.error = null;

    this.errorHandler.tryExecuteAsync(async () => {
      // Get course details from service
      this.course = this.courseService.getCourseById(this.courseId);

      // If course doesn't exist in the service, we'll add placeholder data
      // This is just for demonstration - in a real app this would come from the backend

      this.loading = false;
    }, 'loadCourseDetails').catch(error => {
      this.error = 'Error loading course details';
      this.loading = false;
      this.logger.e('Error loading course details:', error);
    });
  }

  goBack(): void {
    this.router.navigate(['/courses']);
  }

  editCourse(): void {
    this.notification.notify('Edit course feature coming soon', "info", 3000);
  }

  toggleCourseInfo(): void {
    this.courseInfoCollapsed = !this.courseInfoCollapsed;
  }

  toggleVmConfig(): void {
    this.vmConfigCollapsed = !this.vmConfigCollapsed;
  }

  getLabsCount(): number {
    return this.labsList ? this.labsList.length : 0;
  }

  getTotalDuration(): number {
    // Placeholder - in a real app, you would calculate this based on lab durations
    return 15;
  }

  sanitizeUrl(url: string): any {
    return 0;
  }
}
